import React from 'react';
import { t } from 'i18next';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button/index';
import { CloseButton } from '../../components/CloseButton';
import './login.css';

interface ForgotPasswordFormProps {
    setDisplayPage: (t: 'login' | 'singup' | 'forgot') => void;
    forgotPassword: (email: string) => void;
    resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
}

type Step = 'email' | 'code';

export function ForgotPasswordForm({
    setDisplayPage,
    forgotPassword,
    resetPassword
}: ForgotPasswordFormProps): JSX.Element {
    const [email, setEmail] = React.useState('');
    const [code, setCode] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [step, setStep] = React.useState<Step>('email');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleSendCode = () => {
        forgotPassword(email);
        setStep('code');
    };

    const handleResendCode = () => {
        forgotPassword(email);
    };

    const handleResetPassword = async () => {
        if (newPassword !== confirmPassword) {
            setError(t('Les mots de passe ne correspondent pas') ?? '');
            return;
        }
        if (newPassword.length < 6) {
            setError(t('Le mot de passe doit contenir au moins 6 caractères') ?? '');
            return;
        }
        setError('');
        setIsSubmitting(true);
        try {
            await resetPassword(email, code, newPassword);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (step === 'email') {
        return (
            <>
                <CloseButton onClick={() => setDisplayPage('login')} />
                <h3>{t('Réinitialiser mon mot de passe')}</h3>
                <h5>
                    {t(
                        `Saisissez l'adresse email associée à votre compte. Nous vous enverrons un code par email pour réinitialiser votre mot de passe`
                    )}
                </h5>
                <form
                    id="forgot-password"
                    className="reset-form"
                    onSubmit={(e) => e.preventDefault()}>
                    <Input
                        label="Email"
                        type="email"
                        placeholder="ex : jean.dupont@hotmail.com"
                        value={email}
                        onChange={(val: string) => setEmail(val)}
                    />
                    <div className="button-group">
                        <Button
                            label={t('Envoyer le code') ?? ''}
                            theme="PRIMARY"
                            fullWidth
                            action={handleSendCode}
                        />
                    </div>
                </form>
            </>
        );
    }

    return (
        <>
            <CloseButton onClick={() => setDisplayPage('login')} />
            <h3>{t('Nouveau mot de passe')}</h3>
            <h5>{t('Un code à 6 chiffres a été envoyé à')}</h5>
            <h5>
                <strong>{email}</strong>
            </h5>
            <h5>{t('Saisissez-le ci-dessous.')}</h5>
            {error && <div id="error-message">{error}</div>}
            <form id="reset-password" className="reset-form" onSubmit={(e) => e.preventDefault()}>
                <Input
                    label={t('Code de vérification') ?? ''}
                    type="text"
                    placeholder="123456"
                    value={code}
                    onChange={(val: string) => setCode(val.trim())}
                    autoComplete="one-time-code"
                />
                <p className="resend-link" onClick={handleResendCode}>
                    {t('Renvoyer le code')}
                </p>
                <hr className="separator" />
                <Input
                    label={t('Nouveau mot de passe') ?? ''}
                    type="password"
                    placeholder="********"
                    value={newPassword}
                    onChange={(val: string) => setNewPassword(val)}
                    autoComplete="new-password"
                />
                <div className="confirm-field">
                    <Input
                        label={t('Confirmer le mot de passe') ?? ''}
                        type="password"
                        placeholder="********"
                        value={confirmPassword}
                        onChange={(val: string) => setConfirmPassword(val)}
                        autoComplete="new-password"
                    />
                </div>
                <div className="button-group">
                    <Button
                        label={
                            (isSubmitting
                                ? t('Réinitialisation...')
                                : t('Réinitialiser mon mot de passe')) ?? ''
                        }
                        theme="PRIMARY"
                        fullWidth
                        disabled={isSubmitting}
                        action={handleResetPassword}
                    />
                </div>
            </form>
        </>
    );
}
